import type { FunctionComponent } from "react"
import type { Brand, Organization, WithContext } from "schema-dts"

import { usePathname } from "next/navigation"

import { useGetTitleSuffix } from "@/features/companyDetail/hooks/useGetTitleSuffix"
import { useCompanyFetch } from "@/modules/companies/hooks/useCompanyFetch"
import { COMPANY_JOB_OFFERS } from "@/modules/companies/utils/routing"
import { useLocale } from "@/modules/locales/useLocale"
import { MetaTags } from "@/modules/MetaTags/MetaTags"

type Props = {
  baseUrl: string
}

export const SeoSchemaAndMeta: FunctionComponent<Props> = ({ baseUrl }) => {
  const pathname = usePathname()
  const currentLocale = useLocale()
  const { dataCompany } = useCompanyFetch()
  const titleSuffix = useGetTitleSuffix()

  if (!dataCompany) {
    return null
  }

  const {
    availableLocales,
    mainLocale,
    page: { description, headquarterAddress, logoUrl, name, brands, associatedCompanies },
  } = dataCompany

  const logo = logoUrl

  const noParamNoAnchorPathArray = pathname.split("/")
  noParamNoAnchorPathArray.splice(1, 1)
  const noLocalePath = noParamNoAnchorPathArray.join("/")
  const canonicalLocale = availableLocales.includes(currentLocale) ? currentLocale : mainLocale
  const alternateLocales = availableLocales.filter(localeToRemove => localeToRemove !== canonicalLocale)

  const schemaData: WithContext<Organization> = {
    "@context": "https://schema.org",
    "@type": "Organization",
    address: headquarterAddress,
    description,
    logo,
    name,
    url: `${baseUrl}/${canonicalLocale}${noLocalePath}`,
  }

  if (brands) {
    const brandsSchema: Brand[] = brands.map(brand => {
      return {
        "@type": "Brand",
        description: brand.description,
        logo: brand.logo.url,
        name: brand.title,
      }
    })
    schemaData.brand = brandsSchema
  }

  if (associatedCompanies) {
    const parentOrganization: Organization[] = associatedCompanies
      .filter(associatedCompany => associatedCompany.isParent)
      .map(associatedCompany => {
        return {
          "@type": "Organization",
          description: associatedCompany.description,
          logo: associatedCompany.logoUrl,
          name: associatedCompany.name,
          url: `${baseUrl}/${canonicalLocale}/companies/${associatedCompany.permalink}`,
        }
      })
    if (parentOrganization.length > 0) {
      schemaData.parentOrganization = parentOrganization
    }

    const subOrganization: Organization[] = associatedCompanies
      .filter(associatedCompany => associatedCompany.companyPage)
      .map(associatedCompany => {
        return {
          "@type": "Organization",
          description: associatedCompany.description,
          logo: associatedCompany.logoUrl,
          name: associatedCompany.name,
          url: `${baseUrl}/${canonicalLocale}/companies/${associatedCompany.permalink}`,
        }
      })
    if (parentOrganization.length > 0) {
      schemaData.subOrganization = subOrganization
    }
  }

  let canonicalPath = noLocalePath
  const canonicalPathArray = canonicalPath.split("/")

  if (canonicalPathArray[3] !== COMPANY_JOB_OFFERS) {
    canonicalPath = canonicalPathArray.slice(0, 3).join("/")
  }

  /* eslint-disable react/no-danger */
  /* using dangerouslySetInnerHTML to inject structured date seems to be default behavior */
  return (
    <MetaTags title={`${name} - ${titleSuffix}`} description={description} image={logo} location={headquarterAddress}>
      <>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
          key="product-jsonld"
        />
        <link rel="canonical" href={`${baseUrl}/${canonicalLocale}${canonicalPath}`} />
        {alternateLocales.map(alternateLocale => (
          <link
            href={`${baseUrl}/${alternateLocale}${canonicalPath}`}
            hrefLang={alternateLocale}
            key={alternateLocale}
            rel="alternate"
          />
        ))}
      </>
    </MetaTags>
  )
  /* eslint-enable react/no-danger */
}
